import AppPropTypes from '../../shared/appPropTypes'
import CssBaseline from '@mui/material/CssBaseline'
import Fade from '@mui/material/Fade'
import MikeLogin from '../../components/mike-login/MikeLogin'
import IAMNav from '../../components/IAMNav'
import MikeSupportMenuContainer from '@mike/mike-shared-frontend/mike-support'
import { ReactComponent as DataAdmin } from '@mike/mike-shared-frontend/media/icons/DataAdmin'
import MikeTopbar from '@mike/mike-shared-frontend/mike-topbar/mike-topbar'
import ProgressSpinner from '@mike/mike-shared-frontend/mike-topbar-progress-spinner'
import MikeNavWarnBanner from '@mike/mike-shared-frontend/mike-nav-warn-banner/MikeNavWarnBanner'
import MikeSlidingPanel from '../SlidingPanel'
import {
  SystemStatusBanner,
  SystemStatusPill,
  SystemStatusProvider
} from '../SystemStatusBanner'
import PropTypes from 'prop-types'
import React, { CSSProperties, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { styled } from '@mui/material/styles'
import { Box, ButtonGroup, Chip, Popover } from '@mui/material'
import { buttonGroupClasses } from '@mui/material/ButtonGroup'
import CreateTenantForm from '../../components/CreateTenantForm'
import Grid from '@mui/material/Grid'
import ChangeSiteButton from '../buttons/ChangeSiteButton'
import MikeCloudApps from '../MikeCloudApps'
import { SiteOwnersDialogButton } from './SiteOwnersDialogButton'
import { TenantButtons, useTenantButtons } from './TenantButtons'
import { useTypedSelector } from '../../reducers'
import { ExpandMore } from '@mui/icons-material'

const StyledRoot = styled('div')({
  display: 'flex'
})

const StyledAppBarSpacer = styled('div')(({ theme }) => ({
  minHeight: theme.spacing(4.5),
  backgroundColor: (theme as any).palette.lightGrey?.main
}))

const StyledContent = styled('main')(({ theme }) => ({
  flexGrow: 1,
  height: '100vh',
  overflow: 'auto',
  paddingBottom: theme.spacing(3),
  paddingLeft: theme.spacing(0),
  paddingRight: theme.spacing(0),
  paddingTop: theme.spacing(3),
  backgroundColor: (theme as any).palette.lightGrey?.main
}))

const StyledRightActionButtons = styled('div')({
  display: 'flex',
  justifyContent: 'right',
  marginRight: '32px'
})

const APPLICATION_TYPE = 'Application'

const APP_TO_FILTER = [
  'Company Subscription (giving access to Cloud Admin)',
  'Flood Screener',
  'Global Seas'
]

const AppLayout = ({
  children,
  support,
  tutorial,
  progressItems,
  tenants,
  openCreateTenant,
  onCreateTenantCloseClick,
  onCreateTenant,
  onOpenCreateTenant,
  isAdmin,
  loadingApps,
  features,
  currentProject,
  user
}) => {
  // The popover anchors to the chip itself rather than to the clicked element,
  // so opening from the label and from the expand icon look the same. The node
  // lives in state, not a ref, so the anchor is tracked across renders.
  const [tenantChipEl, setTenantChipEl] = useState<HTMLDivElement | null>(null)
  const [isTenantMenuOpen, setIsTenantMenuOpen] = useState(false)

  const handleTenantMenuToggle = () => {
    setIsTenantMenuOpen(open => !open)
  }

  // Chip routes Backspace/Delete keyups to onDelete as well, so the expand icon
  // only toggles on an actual click.
  const handleTenantMenuIconClick = (
    event: React.MouseEvent | React.KeyboardEvent
  ) => {
    if (event.type !== 'click') {
      return
    }

    setIsTenantMenuOpen(open => !open)
  }

  const handleTenantMenuClose = () => {
    setIsTenantMenuOpen(false)
  }

  const handleOpenNewTab = () => {
    const url = new URL('/', window.location.origin)
    url.searchParams.set('changeSite', '1')
    window.open(url.toString(), '_blank', 'noopener,noreferrer')
    setIsTenantMenuOpen(false)
  }

  const intl = useIntl()

  const apps = features.filter(feat => {
    return feat.type === APPLICATION_TYPE && !APP_TO_FILTER.includes(feat.name)
  })

  const tenantName = useTypedSelector(state => {
    const foundTenant = state.tenant.tenants.find(
      tenant => tenant.id === state.auth.user?.tenantId
    )
    return foundTenant?.name || ''
  })

  // The chip is the only way into the site menu, so it must not be gated on a
  // tenant name that may be missing or still loading.
  const tenantChipLabel =
    tenantName || intl.formatMessage({ id: 'appLayout.manageSites' })

  // ButtonGroup derives its first/last corner rounding from its element
  // children, counting those that render null, so the tenant button is gated on
  // the same rule it applies internally.
  const { canCreateTenants } = useTenantButtons({ user, tenants })

  const siteTitle = tenantName || ''
  useEffect(() => {
    const base = 'MIKE Cloud Admin'
    document.title = siteTitle ? `${base} - ${siteTitle}` : base
  }, [siteTitle])

  const mailConfig = {
    to: support.toEmail,
    subject: support.subject,
    body: support.body
  }

  const userManualsConfig = {
    href: 'https://userdoc.mikecloud.mikepoweredbydhi.com/',
    target: '_blank',
    label: 'User Manuals'
  }

  const platformDocumentationConfig = {
    href: tutorial.platformDocumentation,
    target: '_blank',
    label: 'Platform Documentation'
  }

  const portalSupportConfig = {
    href: support.portalLink,
    target: '_blank',
    label: 'Support portal'
  }

  const logoutCallback = React.useCallback(() => {
    const _paq = (window['_paq'] = window['_paq'] || [])
    _paq.push(['resetUserId'])
    _paq.push(['appendToTrackingUrl', 'new_visit=1'])
    _paq.push(['trackPageView'])
    _paq.push(['appendToTrackingUrl', ''])
  }, [])

  const loginProps = () => ({
    logInLabel: 'Log in',
    logOutLabel: 'Log out',
    logoutClickCallback: logoutCallback
  })

  const topBarProps = {
    appIcon: <DataAdmin />,
    appTitle: 'MIKE Cloud Admin',
    loginButton: <MikeLogin {...loginProps()} />,
    supportButton: (
      <MikeSupportMenuContainer
        mailConfig={mailConfig}
        documentationConfig={[
          userManualsConfig,
          platformDocumentationConfig,
          portalSupportConfig
        ]}
        tooltipPosition={{ top: -60, left: -20 }}
      />
    ),
    actionsArea: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
        <SystemStatusPill />
        {progressItems?.length ? (
          <ProgressSpinner progressItems={progressItems} />
        ) : null}
      </div>
    ),
    homeButton: null
  }

  const gridStyles: CSSProperties = {
    position: 'relative'
  }

  const pageHeaderStyle: CSSProperties = {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    flexDirection: 'column',
    marginRight: '2.35rem'
  }

  const popoverContentStyle: CSSProperties = {
    padding: '1rem'
  }

  return (
    <>
      <CssBaseline />

      <SystemStatusProvider>
        <Fade in>
          <StyledRoot>
            <MikeTopbar {...topBarProps} />

            <StyledContent>
              <StyledAppBarSpacer />
              <MikeNavWarnBanner showAlways={false} />
              <SystemStatusBanner />

              <Grid container style={gridStyles}>
                <div style={pageHeaderStyle}>
                  {/* Sized to match the breadcrumb chips (AppBreadcrumbNav). */}
                  <Chip
                    ref={setTenantChipEl}
                    color="secondary"
                    sx={{
                      height: theme => theme.spacing(3),
                      color: '#ffffff',
                      fontWeight: 'bold'
                    }}
                    aria-haspopup="true"
                    aria-expanded={isTenantMenuOpen ? 'true' : undefined}
                    onClick={handleTenantMenuToggle}
                    onDelete={handleTenantMenuIconClick}
                    deleteIcon={
                      <ExpandMore
                        fontSize="small"
                        sx={{
                          transition: 'transform 150ms',
                          transform: isTenantMenuOpen
                            ? 'rotate(180deg)'
                            : 'none'
                        }}
                      />
                    }
                    label={tenantChipLabel}
                  />
                  <Popover
                    anchorEl={tenantChipEl}
                    open={isTenantMenuOpen}
                    onClose={handleTenantMenuClose}
                    // The chip sits at the right edge, so the menu opens
                    // right-aligned under it rather than running off-screen.
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right'
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right'
                    }}
                  >
                    <div style={popoverContentStyle}>
                      {/* variant/color match what the buttons inside declare,
                          so the group's own border rules line up with them. */}
                      <ButtonGroup
                        orientation="vertical"
                        variant="outlined"
                        color="secondary"
                        sx={{
                          // The group overlaps neighbours by 1px, but the MIKE
                          // theme gives outlined buttons a 2px border, so the
                          // shared edges need 2px to collapse into one.
                          [`& .${buttonGroupClasses.middleButton}, & .${buttonGroupClasses.lastButton}`]: {
                            marginTop: '-2px'
                          }
                        }}
                      >
                        {tenants?.length > 1 && (
                          <ChangeSiteButton
                            buttonProps={{}}
                            handleChangeSiteClick={handleOpenNewTab}
                          />
                        )}

                        {canCreateTenants && (
                          <TenantButtons
                            tenants={tenants}
                            user={user}
                            onOpenCreateTenant={onOpenCreateTenant}
                          />
                        )}

                        <SiteOwnersDialogButton />
                      </ButtonGroup>
                    </div>
                  </Popover>
                </div>
                <MikeSlidingPanel
                  position="right"
                  isOpen={openCreateTenant}
                  onClose={onCreateTenantCloseClick}
                  titleArea={intl.formatMessage({
                    id: 'createTenantForm.createTenantButton'
                  })}
                  contentArea={
                    <CreateTenantForm handleCreateTenant={onCreateTenant} />
                  }
                  actionsArea={null}
                  noGrayOverlay={false}
                />
                {user && user.tenantId ? (
                  // The site chip block and the action slot (both right) are out
                  // of flow, so this grows to the full row width and the tabs
                  // start at its left edge, inset like the other tab bars.
                  <Grid
                    item
                    xs
                    container
                    justifyContent="flex-start"
                    alignItems="center"
                    sx={{ pl: '30px' }}
                  >
                    <IAMNav showSiteAccess={isAdmin} />
                  </Grid>
                ) : null}

                <Box
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <StyledRightActionButtons></StyledRightActionButtons>
                </Box>
              </Grid>

              {/*Renders a grid of apps*/}
              <MikeCloudApps
                loading={loadingApps}
                applications={apps}
                projectId={currentProject?.details?.data?.id}
                tenantId={user?.tenantId}
              />

              {children}
            </StyledContent>
          </StyledRoot>
        </Fade>
      </SystemStatusProvider>
    </>
  )
}

AppLayout.propTypes = {
  children: PropTypes.node.isRequired,
  support: AppPropTypes.support.isRequired,
  tutorial: AppPropTypes.tutorial.isRequired,
  user: AppPropTypes.user,
  progressItems: PropTypes.array,
  tenants: PropTypes.array,
  tenantId: PropTypes.string,
  openCreateTenant: PropTypes.bool,
  onCreateTenantCloseClick: PropTypes.func.isRequired,
  onCreateTenant: PropTypes.func.isRequired,
  onOpenCreateTenant: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool,
  loadingApps: PropTypes.bool,
  apps: PropTypes.object,
  project: PropTypes.object,
  features: PropTypes.array,
  currentProject: PropTypes.object
}

export default AppLayout
